import React, { Fragment } from 'react';
import { blankLink } from 'config';
import { Form, FormControl, FormGroup } from 'react-bootstrap';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ActionDeviceTypes } from 'Services/widgets/enums';
import { TooltipPopup } from '../../base-ui-elements/TooltipPopup';

export interface AddDeviceElementProps {
  valField: ActionDeviceTypes;
  insuredId: number;
  onChangeHandler: ( addedNewDevice: ActionDeviceTypes, insuredId: number ) => void;
}

export const AddDeviceElement: React.FC<AddDeviceElementProps> = ( {
  valField,
  insuredId,
  onChangeHandler,
} ) => {
  const { t } = useTranslation( [ 'widgets', 'base' ] );
  const { control, setValue, trigger, formState } = useFormContext();
  const [ showTooltip, setShowTooltip ] = React.useState<boolean>( false );
  const [ bfTooltip, setBfTooltip ] = React.useState<string>( '' );
  const [ valueField, setValueField ] = React.useState<ActionDeviceTypes>( valField );

  const tooltipInfo = t( 'bookingFunnel.tariff.tooltipInfo' );

  const onShowTooltip = React.useCallback ( ( isShow: boolean, tooltip: string ): void => {
    setShowTooltip( isShow );
    setBfTooltip( tooltip );
  }, [] );

  const handleChange = React.useCallback( ( e, fieldName: string ) => {
    trigger();

    if ( !formState.isValid ) {
      return;
    }

    const value: ActionDeviceTypes = e.target.value;
    onChangeHandler( value, insuredId );
    setValueField( value );

    setValue( fieldName, value, { shouldValidate: true } );
  }, [ formState.isValid, insuredId, onChangeHandler, setValue, trigger ] );

  return (
    <Fragment>
      { valueField === ActionDeviceTypes.No && (
        <Fragment>
          <Form.Label className="tooltip-label">
            { t( 'bookingFunnel.tariff.addNewDeviceTitle' ) }
            <a
              id={ `tooltip-${insuredId}` }
              href={ blankLink }
              onClick={ ( e ) => {
                e.preventDefault();
                onShowTooltip( true, tooltipInfo );
              } }
            >
              { t( 'bookingFunnel.tooltipHelp' ) }
            </a>
          </Form.Label>
          <Controller
            name={ `add-new-device_${insuredId}` }
            control={ control }
            defaultValue={ valueField }
            render={ ( props ) => (
              <FormGroup
                className={ `custom-btn-radio mb-2 rdb-${props.name} 
            ${ props.value === ActionDeviceTypes.Yes && 'gm-disabled-box' }` }
              >
                <FormControl
                  { ...props }
                  className="form-check-input"
                  type="radio"
                  id={ `option-yes-${ props.name}` }
                  value={ ActionDeviceTypes.Yes }
                  checked={ props.value === ActionDeviceTypes.Yes }
                  onChange={ ( e ) => handleChange( e, props.name ) }
                  onBlur={ props.onBlur }
                />
                <Form.Label
                  className="btn btn-border-radio"
                  htmlFor={ `option-yes-${ props.name}` }
                >
                  { t( 'bookingFunnel.tariff.yes' ) }
                </Form.Label>
                <FormControl
                  { ...props }
                  className="form-check-input"
                  type="radio"
                  id={ `option-no-${ props.name }` }
                  value={ ActionDeviceTypes.No }
                  checked={ props.value === ActionDeviceTypes.No }
                  onChange={ ( e ) => handleChange( e, props.name ) }
                  onBlur={ props.onBlur }
                />
                <Form.Label
                  className="btn btn-border-radio"
                  htmlFor={ `option-no-${ props.name }` }
                >
                  { t( 'bookingFunnel.tariff.later' ) }
                </Form.Label>
              </FormGroup>
            ) }
          />
          { showTooltip && bfTooltip && (
            <TooltipPopup
              tooltipInfo={ bfTooltip }
              onClose={ () => onShowTooltip( false, '' ) }
            />
          ) }
        </Fragment>
      ) }
    </Fragment>
  );
};
